import { useMemo, useState } from "react";
import { Check, Pencil, Plus, Search, Settings2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  useProductGroups,
  useCreateProductGroup,
  useUpdateProductGroup,
  useDeleteProductGroup,
  type ProductGroup,
} from "../../features/brands/queries";
import { showDeleteConfirm } from "./DeleteConfirm";
import { cn } from "@/lib/utils";
import { errorMessage } from "@/lib/error-message";

const lower = (s: string) => s.toLocaleLowerCase("tr");

/**
 * TR↔EN eşleşmeli ürün grubu çoklu seçimi.
 * Sözlük (ProductGroup tablosu) API'den gelir; seçim her zaman TR adlarıyla tutulur,
 * EN karşılıkları sözlükten türetilir. `lang="en"` iken aynı seçim İngilizce
 * adlarla gösterilir — TR'de seçilen, EN'de otomatik seçilidir.
 * "Yönet" ile sözlük düzenlenir.
 */
export function ProductGroupSelect({
  value,
  onChange,
  lang = "tr",
}: {
  /** Seçili TR adları (virgülle ayrılmış — DB formatı) */
  value: string;
  onChange: (v: string) => void;
  /** Görüntüleme dili — seçim TR bazlı kalır, sadece etiketler değişir */
  lang?: "tr" | "en";
}) {
  const { data: groups = [], isLoading } = useProductGroups();
  const [search, setSearch] = useState("");
  const [manageOpen, setManageOpen] = useState(false);

  const selected = value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const selectedSet = new Set(selected.map(lower));

  const dict = useMemo(() => new Map(groups.map((g) => [lower(g.nameTr), g])), [groups]);

  const filtered = useMemo(() => {
    const q = lower(search.trim());
    if (!q) return groups;
    return groups.filter((g) => lower(g.nameTr).includes(q) || lower(g.nameEn).includes(q));
  }, [groups, search]);

  function toggle(nameTr: string) {
    if (selectedSet.has(lower(nameTr))) {
      onChange(selected.filter((s) => lower(s) !== lower(nameTr)).join(","));
    } else {
      onChange([...selected, nameTr].join(","));
    }
  }

  return (
    <div className="space-y-2">
      {/* Seçili gruplar */}
      <div className="flex flex-wrap gap-1.5 min-h-9 rounded-md border px-2 py-1.5">
        {selected.length === 0 && (
          <span className="text-sm text-muted-foreground py-0.5">
            Aşağıdaki listeden ürün grubu seçin
          </span>
        )}
        {selected.map((tr) => {
          const en = dict.get(lower(tr))?.nameEn;
          // EN modunda İngilizce ad öne çıkar; TR karşılığı ikincil gösterilir
          const primary = lang === "en" ? en || tr : tr;
          const secondary = lang === "en" ? tr : en;
          return (
            <Badge key={tr} variant="default" className="gap-1 pr-1">
              <span>
                {primary}
                {secondary && secondary !== primary && (
                  <span className="opacity-70 font-normal"> · {secondary}</span>
                )}
              </span>
              <button
                type="button"
                onClick={() => toggle(tr)}
                className="rounded-full hover:bg-primary-foreground/20 p-0.5"
                aria-label={`${primary} kaldır`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          );
        })}
      </div>

      {/* Arama + yönet */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-64">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Grup ara..."
            className="pl-8 h-8"
          />
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => setManageOpen(true)}>
          <Settings2 className="h-4 w-4 mr-1.5" />
          Grupları Yönet
        </Button>
      </div>

      {/* Seçim listesi */}
      <ScrollArea className="h-44 rounded-md border">
        <div className="p-1">
          {isLoading ? (
            <p className="text-sm text-muted-foreground p-2">Yükleniyor…</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground p-2">
              Eşleşen grup yok — "Grupları Yönet" ile ekleyebilirsiniz.
            </p>
          ) : (
            filtered.map((g) => {
              const isSelected = selectedSet.has(lower(g.nameTr));
              const primary = lang === "en" ? g.nameEn || g.nameTr : g.nameTr;
              const secondary = lang === "en" ? g.nameTr : g.nameEn || "EN karşılığı yok";
              return (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => toggle(g.nameTr)}
                  className={cn(
                    "w-full flex items-center gap-2 rounded px-2 py-1.5 text-sm text-left transition-colors",
                    isSelected ? "bg-primary/10" : "hover:bg-muted/60",
                  )}
                >
                  <span
                    className={cn(
                      "flex h-4 w-4 items-center justify-center rounded-sm border shrink-0",
                      isSelected
                        ? "bg-primary border-primary text-primary-foreground"
                        : "border-input",
                    )}
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                  </span>
                  <span className="truncate">{primary}</span>
                  {secondary !== primary && (
                    <span className="text-muted-foreground text-xs truncate ml-auto">
                      {secondary}
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
      </ScrollArea>

      <ProductGroupManager open={manageOpen} onClose={() => setManageOpen(false)} />
    </div>
  );
}

/** Sözlük yönetimi: TR↔EN çiftleri ekle / düzenle / sil */
function ProductGroupManager({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: groups = [] } = useProductGroups();
  const createMutation = useCreateProductGroup();
  const updateMutation = useUpdateProductGroup();
  const deleteMutation = useDeleteProductGroup();

  const [newTr, setNewTr] = useState("");
  const [newEn, setNewEn] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTr, setEditTr] = useState("");
  const [editEn, setEditEn] = useState("");

  function handleAdd() {
    if (!newTr.trim()) return;
    createMutation.mutate(
      { nameTr: newTr.trim(), nameEn: newEn.trim() },
      {
        onSuccess: () => {
          toast.success("Ürün grubu eklendi");
          setNewTr("");
          setNewEn("");
        },
        onError: (e: unknown) => toast.error(errorMessage(e, "Eklenemedi")),
      },
    );
  }

  function startEdit(g: ProductGroup) {
    setEditingId(g.id);
    setEditTr(g.nameTr);
    setEditEn(g.nameEn);
  }

  function handleSaveEdit() {
    if (!editingId || !editTr.trim()) return;
    updateMutation.mutate(
      { id: editingId, nameTr: editTr.trim(), nameEn: editEn.trim() },
      {
        onSuccess: () => {
          toast.success("Güncellendi");
          setEditingId(null);
        },
        onError: (e: unknown) => toast.error(errorMessage(e, "Güncellenemedi")),
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Ürün Gruplarını Yönet</DialogTitle>
        </DialogHeader>

        {/* Yeni grup ekleme */}
        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-1">
            <Label className="text-xs">Türkçe ad</Label>
            <Input
              value={newTr}
              onChange={(e) => setNewTr(e.target.value)}
              placeholder="ör. EKRAN KARTLARI"
              className="h-8"
            />
          </div>
          <div className="flex-1 space-y-1">
            <Label className="text-xs">İngilizce karşılığı</Label>
            <Input
              value={newEn}
              onChange={(e) => setNewEn(e.target.value)}
              placeholder="ör. GRAPHICS CARDS"
              className="h-8"
            />
          </div>
          <Button
            type="button"
            size="sm"
            disabled={!newTr.trim() || createMutation.isPending}
            onClick={handleAdd}
          >
            <Plus className="h-4 w-4 mr-1" />
            Ekle
          </Button>
        </div>

        {/* Mevcut gruplar */}
        <ScrollArea className="h-80 rounded-md border">
          <div className="divide-y">
            {groups.map((g) =>
              editingId === g.id ? (
                <div key={g.id} className="flex items-center gap-2 p-2">
                  <Input
                    value={editTr}
                    onChange={(e) => setEditTr(e.target.value)}
                    className="h-8 flex-1"
                  />
                  <Input
                    value={editEn}
                    onChange={(e) => setEditEn(e.target.value)}
                    className="h-8 flex-1"
                    placeholder="EN karşılığı"
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleSaveEdit}
                    disabled={updateMutation.isPending}
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingId(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div key={g.id} className="flex items-center gap-2 p-2 text-sm">
                  <span className="flex-1 truncate">{g.nameTr}</span>
                  <span className="flex-1 truncate text-muted-foreground">{g.nameEn || "—"}</span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => startEdit(g)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() =>
                      showDeleteConfirm({
                        title: "Ürün Grubu Sil",
                        description: `"${g.nameTr}" sözlükten silinecek. Markalardaki mevcut kayıtlar etkilenmez.`,
                        onConfirm: () =>
                          deleteMutation.mutate(g.id, {
                            onSuccess: () => toast.success("Silindi"),
                            onError: (e: unknown) => toast.error(errorMessage(e, "Silinemedi")),
                          }),
                      })
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ),
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
